import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, Button, Card } from 'react-bootstrap';

import { http } from '../../features/jobs/api/http';
import { fetchJob } from '../../features/jobs/api/jobsApi';
import { rebuildStandardDataFromJob } from '../../helpers/RebuildStandardDataFromJob';
import CreateWizzardForm from '../../components/project-management/create-new/CreateWizzardForm';

const num = v => (Number.isFinite(Number(v)) ? Number(v) : 0);

// Re-open a LAUNCHED phase in the wizard to edit its PENDING drums (change
// length / add / remove / split / drum-Ø / machine). Produced drums are shown
// locked. Submit → POST /jobs/:id/edit-phase which replaces only the PENDING
// tasks/tokens/queue and re-syncs the machine queue.
const EditPhaseWizzard = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Multi-select from the queue: an explicit set of products to edit/merge together.
  const explicitJobIds = Array.isArray(location?.state?.jobIds)
    ? location.state.jobIds.map(String)
    : null;
  const jobId = location?.state?.jobId || explicitJobIds?.[0] || '';
  const phase = location?.state?.phase || '';
  // Group-by hint from the queue-edit page ({ phase: 'color'|'wireDiameter' }): when
  // the selected products were launched grouped by color/Ø, pre-enable the toggle so
  // editing PRESERVES the grouping by default (deselect it to launch individually).
  const groupByHint =
    location?.state?.groupBy && typeof location.state.groupBy === 'object'
      ? location.state.groupBy
      : {};
  // Where to go after submit/back — the machine queue when launched from there,
  // otherwise the product's detail page.
  const returnTo =
    location?.state?.returnTo ||
    `/apps/project-management/project-details/${jobId}`;

  const [standardsData, setStandardsData] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!jobId || !phase) {
        setLoadErr('Missing job or phase.');
        return;
      }
      try {
        setLoading(true);
        const job = await fetchJob(jobId);
        if (!alive) return;
        if (!job?.jobId) throw new Error('Job not found.');

        // Merge partners: the set of jobIds that share any drum with this one.
        let partners = {};
        const groupJobIds = new Set([String(jobId)]);
        try {
          const res = await http.get(`/api/redis/jobs/${jobId}/merge-partners`);
          partners = res?.data?.partners || {};
          for (const list of Object.values(partners))
            for (const p of list) if (p?.jobId) groupJobIds.add(String(p.jobId));
        } catch {
          /* not merged / partners unavailable */
        }

        // Multi-select from the queue: use EXACTLY the chosen products (don't
        // auto-expand via merge partners — the user picked the set to merge/edit).
        if (explicitJobIds && explicitJobIds.length) {
          groupJobIds.clear();
          for (const g of explicitJobIds) groupJobIds.add(String(g));
        }

        // Tag each merged drum with the products sharing it (for the card note).
        const enrich = item => {
          const tag = ex => {
            if (ex?._mergeQueueKey)
              ex._mergePartners = partners[ex._mergeQueueKey] || [];
          };
          for (const p of item?.phases || []) {
            if (Array.isArray(p.conductors))
              for (const c of p.conductors)
                for (const ex of c.execution || []) tag(ex);
            for (const ex of p.execution || []) tag(ex);
          }
          return item;
        };

        // Build one MULTI-PRODUCT standardData: the source product + every other
        // product in the merge group, so the whole merge is edited together.
        const build = j =>
          enrich(
            (rebuildStandardDataFromJob(j, [], phase, {
              editMode: true,
              editPhaseName: phase
            }) || [])[0]
          );

        const items = [build(job)];
        for (const gid of groupJobIds) {
          if (String(gid) === String(jobId)) continue;
          try {
            const gjob = await fetchJob(gid);
            if (gjob?.jobId) items.push(build(gjob));
          } catch {
            /* skip a partner that fails to load */
          }
        }
        for (const it of items) it._mergeGroupJobIds = [...groupJobIds];

        if (!alive) return;
        setStandardsData(items);
      } catch (e) {
        if (alive) setLoadErr(e?.message || String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // explicitJobIds is derived from the (stable) navigation state; jobId reflects it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, phase]);

  const clearStandardsData = useCallback(() => {
    setStandardsData(null);
    navigate(returnTo);
  }, [navigate, returnTo]);

  const handleEditSubmit = useCallback(
    async ({
      standardData,
      editFurther = true,
      mergedPhases = {},
      groupByPhases = {}
    }) => {
      // Multi-product: the source product + every other product in the merge group.
      const items = Array.isArray(standardData) ? standardData : [standardData];

      // Build ONE product's edit-phase payload (edited phase + optional downstream).
      const buildPayload = sd => {
        const editPhaseName = sd?._editPhaseName || phase;
        const phases = sd?.phases || [];
        const editIdx = phases.findIndex(p => p?.name === editPhaseName);
        const ph = editIdx >= 0 ? phases[editIdx] : null;
        if (!ph) return null;

        // exec id → EXISTING tokenId (per product) so a drum's selected upstream
        // resolves to real token ids; new upstreams chain via inputWizardIds.
        const execIdToTokenId = {};
        for (const p of phases) {
          const all = [];
          if (Array.isArray(p.conductors))
            for (const c of p.conductors) for (const ex of c.execution || []) all.push(ex);
          for (const ex of p.execution || []) all.push(ex);
          for (const ex of all)
            if (ex?.id != null && ex.tokenId) execIdToTokenId[String(ex.id)] = ex.tokenId;
        }

        const collectExecs = phaseObj => {
          const out = [];
          const pushExec = (exec, conductorColor) => {
            if (!exec) return;
            const wizardIds = Array.isArray(exec.fromUpstreams)
              ? exec.fromUpstreams.map(u => u?.id).filter(v => v != null)
              : Array.isArray(exec.fromUpstreamIds)
                ? exec.fromUpstreamIds
                : exec.fromUpstreamId != null
                  ? [exec.fromUpstreamId]
                  : [];
            out.push({
              tokenId: exec.tokenId || null,
              plannedLength: num(exec.plannedLength),
              machineId: String(exec.machineId || '').trim(),
              drum_material: exec.drum_material || null,
              drum_diameter: num(exec.drum_diameter) || null,
              conductorColor: conductorColor || exec.color || null,
              isProduced: !!exec._produced,
              merged: !!exec._merged,
              wizardExecId: exec.id ?? null,
              inputWizardIds: wizardIds,
              inputTokenIds: wizardIds
                .map(id => execIdToTokenId[String(id)])
                .filter(Boolean),
              ropeIndex: exec.ropeIndex ?? null,
              wireDiameter: exec.wireDiameter ?? null,
              numberOfWires: exec.numberOfWires ?? null
            });
          };
          if (Array.isArray(phaseObj.conductors))
            for (const cond of phaseObj.conductors)
              for (const exec of cond.execution || [])
                pushExec(exec, cond.isolation?.color || null);
          for (const exec of phaseObj.execution || []) pushExec(exec, null);
          return out;
        };

        const downstream = [];
        if (editFurther && editIdx >= 0) {
          for (let i = editIdx + 1; i < phases.length; i++) {
            const p = phases[i];
            if (!p?.name) continue;
            const execs = collectExecs(p).filter(e => !e.isProduced);
            if (execs.length) downstream.push({ phase: p.name, executions: execs });
          }
        }

        return {
          jobId: sd?._id || sd?._parentJobId,
          phase: editPhaseName,
          executions: collectExecs(ph),
          downstream
        };
      };

      // Each product keeps its OWN length on a shared drum — a shared drum's total is
      // the SUM of the products' contributions, not one value forced on all. So we do
      // NOT coordinate lengths: changing a color's length on one product changes only
      // that product's copy (backend updates it in-place, keeping the shared tokenId).
      const payloads = items.map(sd => buildPayload(sd)).filter(p => p && p.jobId);

      // MERGE Execution ON for a phase → give matching drums (same color + slot
      // position) across the selected products the SAME new token id + merge key, so
      // they combine onto one shared drum. Already-merged drums keep their token; only
      // currently-separate drums are combined. Backend recreates them on the shared id.
      const genShared = () => 'K-' + Math.random().toString(16).slice(2, 10);
      const sharedByKey = {};
      for (const p of payloads) {
        if (!mergedPhases?.[p.phase]) continue;
        const slotByColor = {};
        for (const e of p.executions) {
          if (e.isProduced || e.merged) continue; // only separate drums merge
          const color = String(e.conductorColor || '').toUpperCase() || 'NA';
          const slot = slotByColor[color] || 0;
          slotByColor[color] = slot + 1;
          const key = `${p.phase}::${color}::slot_${slot}`;
          if (!sharedByKey[key])
            sharedByKey[key] = {
              mergeTokenId: genShared(),
              mergeQueueKey: `MERGE::${key}`
            };
          e.mergeTokenId = sharedByKey[key].mergeTokenId;
          e.mergeQueueKey = sharedByKey[key].mergeQueueKey;
        }
      }

      // Sequential to avoid write races on shared machine queues.
      let last;
      for (const p of payloads) {
        const res = await http.post(`/api/redis/jobs/${p.jobId}/edit-phase`, {
          phase: p.phase,
          executions: p.executions,
          // Merge Execution toggle per phase — if turned OFF, the backend un-merges
          // that phase (each product gets an independent drum, sequential in queue).
          mergedPhases,
          // Group-by ordering toggle per phase — when ON, drums stay independent but
          // the queue re-buckets same-color / same-Ø drums together across products
          // (block-by-color). When OFF/absent → individual per product (position kept).
          groupByPhases,
          ...(p.downstream.length ? { downstream: p.downstream } : {})
        });
        last = res?.data;
      }
      navigate(returnTo);
      return last;
    },
    [phase, navigate, returnTo]
  );

  if (standardsData) {
    return (
      <CreateWizzardForm
        standardData={standardsData}
        setStandardsData={setStandardsData}
        clearStandardsData={clearStandardsData}
        handleSubmitFc={handleEditSubmit}
        initialGroupByPhases={groupByHint}
        mode="edit"
      />
    );
  }

  return (
    <Card className="mb-9 mt-3">
      <Card.Body>
        <h3 className="mb-2">Edit launched phase</h3>
        {loadErr ? (
          <Alert variant="danger" className="py-2">
            {loadErr}
          </Alert>
        ) : (
          <div className="text-muted">
            {loading ? 'Loading job…' : 'Preparing editor…'}
          </div>
        )}
        <Button
          className="mt-3"
          variant="phoenix-secondary"
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
      </Card.Body>
    </Card>
  );
};

export default EditPhaseWizzard;
